import { useCallback, useState, useMemo } from 'react';
import { BackofficeUser, RoleInfo, UserSortKey } from '../types';
import * as usersService from '../services/users.service';
import { withAuthRetry } from '../utils/withAuthRetry';

interface UseUsersProps {
  accessToken?: string;
  refreshToken?: string;
  attemptRefresh: () => Promise<boolean>;
  isAdmin: boolean;
}

export function useUsers({ accessToken, refreshToken, attemptRefresh, isAdmin }: UseUsersProps) {
  const [users, setUsers] = useState<BackofficeUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearch, setUserSearch] = useState('');

  // Role catalog
  const [roleCatalog, setRoleCatalog] = useState<RoleInfo[]>([]);
  const [rolesLoading, setRolesLoading] = useState(false);

  // Edit roles dialog
  const [selectedUser, setSelectedUser] = useState<BackofficeUser | null>(null);
  const [editRolesDialogOpen, setEditRolesDialogOpen] = useState(false);
  const [editRoles, setEditRoles] = useState<string[]>([]);
  const [editRolesLoading, setEditRolesLoading] = useState(false);

  // Reset password dialog
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);

  // Delete user dialog
  const [deleteUserDialogOpen, setDeleteUserDialogOpen] = useState(false);
  const [deleteUserLoading, setDeleteUserLoading] = useState(false);

  // Sorting
  const [usersSort, setUsersSort] = useState<{
    column: UserSortKey;
    direction: 'asc' | 'desc';
  }>({
    column: 'name',
    direction: 'asc'
  });

  const fetchUsers = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    if (!accessToken || !isAdmin) return { success: false, error: 'Não autorizado' };

    setUsersLoading(true);

    try {
      const data = await withAuthRetry(
        () => usersService.fetchUsers(accessToken!),
        refreshToken,
        attemptRefresh
      );
      setUsers(data);
      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Erro inesperado ao buscar usuários'
      };
    } finally {
      setUsersLoading(false);
    }
  }, [accessToken, refreshToken, attemptRefresh, isAdmin]);

  const fetchRoles = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    if (!accessToken || !isAdmin) return { success: false, error: 'Não autorizado' };

    setRolesLoading(true);

    try {
      const data = await withAuthRetry(
        () => usersService.fetchRoles(accessToken!),
        refreshToken,
        attemptRefresh
      );
      setRoleCatalog(data);
      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Erro inesperado ao buscar papéis'
      };
    } finally {
      setRolesLoading(false);
    }
  }, [accessToken, refreshToken, attemptRefresh, isAdmin]);

  const updateUserRoles = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    if (!accessToken || !selectedUser) return { success: false, error: 'Não autenticado' };

    setEditRolesLoading(true);

    try {
      await withAuthRetry(
        () => usersService.updateUserRoles(selectedUser.id, editRoles, accessToken!),
        refreshToken,
        attemptRefresh
      );

      setEditRolesDialogOpen(false);
      setSelectedUser(null);
      setEditRoles([]);
      await fetchUsers();
      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Erro ao atualizar papéis do usuário'
      };
    } finally {
      setEditRolesLoading(false);
    }
  }, [accessToken, refreshToken, attemptRefresh, selectedUser, editRoles, fetchUsers]);

  const resetUserPassword = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    if (!accessToken || !selectedUser) return { success: false, error: 'Não autenticado' };

    setResetPasswordLoading(true);

    try {
      await withAuthRetry(
        () => usersService.resetUserPassword(selectedUser.id, newPassword, accessToken!),
        refreshToken,
        attemptRefresh
      );

      setResetPasswordDialogOpen(false);
      setSelectedUser(null);
      setNewPassword('');
      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Erro ao redefinir senha'
      };
    } finally {
      setResetPasswordLoading(false);
    }
  }, [accessToken, refreshToken, attemptRefresh, selectedUser, newPassword]);

  const toggleUserStatus = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    if (!accessToken || !selectedUser) return { success: false, error: 'Não autenticado' };

    setDeleteUserLoading(true);

    try {
      await withAuthRetry(
        () => usersService.toggleUserStatus(selectedUser.id, accessToken!),
        refreshToken,
        attemptRefresh
      );

      setDeleteUserDialogOpen(false);
      setSelectedUser(null);
      await fetchUsers();
      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Erro ao alterar status do usuário'
      };
    } finally {
      setDeleteUserLoading(false);
    }
  }, [accessToken, refreshToken, attemptRefresh, selectedUser, fetchUsers]);

  const deleteUser = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    if (!accessToken || !selectedUser) return { success: false, error: 'Não autenticado' };

    setDeleteUserLoading(true);

    try {
      await withAuthRetry(
        () => usersService.deleteUser(selectedUser.id, accessToken!),
        refreshToken,
        attemptRefresh
      );

      setDeleteUserDialogOpen(false);
      setSelectedUser(null);
      await fetchUsers();
      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Erro ao deletar usuário'
      };
    } finally {
      setDeleteUserLoading(false);
    }
  }, [accessToken, refreshToken, attemptRefresh, selectedUser, fetchUsers]);

  const toggleUsersSort = useCallback((column: UserSortKey) => {
    setUsersSort((current) =>
      current.column === column
        ? { column, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { column, direction: 'asc' }
    );
  }, []);

  const openEditRolesDialog = useCallback((user: BackofficeUser) => {
    setSelectedUser(user);
    setEditRoles(user.roles);
    setEditRolesDialogOpen(true);
  }, []);

  const openResetPasswordDialog = useCallback((user: BackofficeUser) => {
    setSelectedUser(user);
    setNewPassword('');
    setResetPasswordDialogOpen(true);
  }, []);

  const openDeleteUserDialog = useCallback((user: BackofficeUser) => {
    setSelectedUser(user);
    setDeleteUserDialogOpen(true);
  }, []);

  const sortedUsers = useMemo(() => {
    const filtered = users.filter(
      (user) =>
        !userSearch ||
        user.name.toLowerCase().includes(userSearch.toLowerCase()) ||
        user.email.toLowerCase().includes(userSearch.toLowerCase()) ||
        user.username.toLowerCase().includes(userSearch.toLowerCase())
    );

    const direction = usersSort.direction === 'asc' ? 1 : -1;

    return filtered.sort((a, b) => {
      if (usersSort.column === 'status') {
        return ((a.isActive ? 1 : 0) - (b.isActive ? 1 : 0)) * direction;
      }
      if (usersSort.column === 'roles') {
        return (a.roles.length - b.roles.length) * direction;
      }
      if (usersSort.column === 'createdAt') {
        return (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()) * direction;
      }
      const aVal = a[usersSort.column];
      const bVal = b[usersSort.column];
      return String(aVal).localeCompare(String(bVal)) * direction;
    });
  }, [users, userSearch, usersSort]);

  return {
    // State
    users,
    sortedUsers,
    usersLoading,
    userSearch,
    setUserSearch,
    usersSort,
    roleCatalog,
    rolesLoading,

    // Dialog state
    selectedUser,
    editRolesDialogOpen,
    setEditRolesDialogOpen,
    editRoles,
    setEditRoles,
    editRolesLoading,
    resetPasswordDialogOpen,
    setResetPasswordDialogOpen,
    newPassword,
    setNewPassword,
    resetPasswordLoading,
    deleteUserDialogOpen,
    setDeleteUserDialogOpen,
    deleteUserLoading,

    // Actions
    fetchUsers,
    fetchRoles,
    updateUserRoles,
    resetUserPassword,
    toggleUserStatus,
    deleteUser,
    toggleUsersSort,
    openEditRolesDialog,
    openResetPasswordDialog,
    openDeleteUserDialog
  };
}
